import * as React from 'react'
import { IOWidget, Updater, Widget, fromJSX } from 'widgets-for-react'
import { PublicSiteState } from "../../../../../root/state"
import { BlockData, CMSState } from './types.gen'
import { DownloadBlockProps } from './widgets/downloadBlock.gen'
import { DownloadBlockNestedProps } from './widgets/downloadBlockNested.gen'
import {
  FeaturedContentBlockProps,
  FeaturedContentBlockStyle
} from './widgets/featuredContentBlock.gen'
import { IframeBlockProps, IframeBlockStyle } from './widgets/iframeBlock.gen'
import { ImageGalleryBlockProps, ImageGalleryBlockStyle } from './widgets/imageGalleryBlock.gen'
import { LinkBlockProps, LinkBlockStyle } from './widgets/linkBlock.gen'
import { LinkGridBlockProps } from './widgets/linkGridBlock.gen'
import { MediaBlockProps } from './widgets/mediaBlock.gen'
import { MediaBlockNestedProps } from './widgets/mediaBlockNested.gen'
import { QuoteBlockProps } from './widgets/quoteBlock.gen'
import { QuoteBlockNestedProps } from './widgets/quoteBlockNested.gen'
import { TextBlockNestedProps } from './widgets/textBlockNested.gen'
import { TextImageBlockProps, TextImageBlockStyle } from './widgets/textImageBlock.gen'
import { TextReadMoreBlockProps, TextReadMoreBlockStyle } from './widgets/textReadMoreBlock.gen'

export type BlocksRenderer = {
  featuredContentBlock: (
    props: FeaturedContentBlockProps,
    options?: { style?: FeaturedContentBlockStyle; key?: string }
  ) => IOWidget<
    CMSState,
    Updater<CMSState>
  >
  textImageBlock: (
    props: TextImageBlockProps,
    options?: { style?: TextImageBlockStyle; key?: string }
  ) => IOWidget<
    CMSState,
    Updater<CMSState>
  >
  mediaBlock: (
    props: MediaBlockProps
  ) => (
    s: CMSState,
    set_public_site_state: (output: Updater<PublicSiteState>) => void
  ) => (public_state: PublicSiteState) => Widget<Updater<CMSState>>
  mediaBlockNested: (
    props: MediaBlockNestedProps
  ) => IOWidget<
    CMSState,
    Updater<CMSState>
  >
  linkGridBlock: (
    props: LinkGridBlockProps
  ) => (
    viewportWidth: number,
    s: CMSState
  ) => Widget<Updater<CMSState>>
  linkBlock: (
    props: LinkBlockProps,
    options?: { style?: LinkBlockStyle; key?: string }
  ) => IOWidget<
    CMSState,
    Updater<CMSState>
  >
  downloadBlock: (
    props: DownloadBlockProps
  ) => IOWidget<
    CMSState,
    Updater<CMSState>
  >
  downloadBlockNested: (
    props: DownloadBlockNestedProps
  ) => IOWidget<
    CMSState,
    Updater<CMSState>
  >
  textBlockNested: (
    props: TextBlockNestedProps
  ) => IOWidget<
    CMSState,
    Updater<CMSState>
  >
  textReadMoreBlock: (
    props: TextReadMoreBlockProps,
    options?: { style?: TextReadMoreBlockStyle; key?: string }
  ) => IOWidget<
    CMSState,
    Updater<CMSState>
  >
  quoteBlock: (
    props: QuoteBlockProps
  ) => IOWidget<
    CMSState,
    Updater<CMSState>
  >
  quoteBlockNested: (
    props: QuoteBlockNestedProps
  ) => IOWidget<
    CMSState,
    Updater<CMSState>
  >
  imageGalleryBlock: (
    props: ImageGalleryBlockProps,
    options?: { style?: ImageGalleryBlockStyle; key?: string }
  ) => IOWidget<
    CMSState,
    Updater<CMSState>
  >
  iframeBlock: (
    props: IframeBlockProps,
    options?: { style?: IframeBlockStyle; key?: string }
  ) => (
    set_public_site_state: (output: Updater<PublicSiteState>) => void
  ) => (public_state: PublicSiteState) => Widget<Updater<CMSState>>
}

export function renderBlock(
  block: BlockData,
  block_renderer: BlocksRenderer,
  set_public_site_state: (output: Updater<PublicSiteState>) => void,
  public_state: PublicSiteState,
  key?: string
): IOWidget<
  CMSState,
  Updater<CMSState>
> {
  return (s0) =>
    block.kind === 'featuredContentBlock'
      ? block_renderer.featuredContentBlock(block.value[0], { style: block.value[1], key })(s0)
      : block.kind === 'textImageBlock'
      ? block_renderer.textImageBlock(block.value[0], { style: block.value[1], key })(s0)
      : block.kind === 'imageGalleryBlock'
      ? block_renderer.imageGalleryBlock(block.value[0], { style: block.value[1], key })(s0)
      : block.kind === 'textBlockNested'
      ? block_renderer.textBlockNested(block.value)(s0)
      : block.kind === 'textReadMoreBlock'
      ? block_renderer.textReadMoreBlock(block.value[0], { style: block.value[1], key })(s0)
      : block.kind === 'downloadBlock'
      ? block_renderer.downloadBlock(block.value)(s0)
      : block.kind === 'downloadBlockNested'
      ? block_renderer.downloadBlockNested(block.value)(s0)
      : block.kind === 'linkBlock'
      ? block_renderer.linkBlock(block.value[0], { style: block.value[1], key })(s0)
      : block.kind === 'linkGridBlock'
      ? block_renderer.linkGridBlock(block.value)(public_state.viewportWidth, s0)
      : block.kind === 'mediaBlock'
      ? block_renderer.mediaBlock(block.value)(s0, set_public_site_state)(public_state)
      : block.kind === 'mediaBlockNested'
      ? block_renderer.mediaBlockNested(block.value)(s0)
      : block.kind === 'quoteBlock'
      ? block_renderer.quoteBlock(block.value)(s0)
      : block.kind === 'quoteBlockNested'
      ? block_renderer.quoteBlockNested(block.value)(s0)
      : block.kind === 'iframeBlock'
      ? block_renderer.iframeBlock(block.value[0], { style: block.value[1], key })(set_public_site_state)(public_state)
      : fromJSX(() => (
          <div key={key}>Block not found</div>
        )).never()
}
